import { useEffect, useState } from "react";
import { getNodeCG } from "./client.js";

/**
 * Subscribe to a NodeCG replicant. Returns the current value and a setter
 * that proposes a change (replicant.value = ...). The hook re-renders on
 * every replicant change; if the replicant isn't available yet (NodeCG not
 * loaded), the value is `undefined`.
 */
export function useReplicant<T>(
  name: string,
): [T | undefined, (next: T) => void] {
  const [value, setValue] = useState<T | undefined>(() => {
    const nodecg = getNodeCG();
    return nodecg ? (nodecg.Replicant<T>(name).value ?? undefined) : undefined;
  });

  useEffect(() => {
    const nodecg = getNodeCG();
    if (!nodecg) return;
    const rep = nodecg.Replicant<T>(name);
    const handler = (newVal: T | undefined) => {
      setValue(newVal);
    };
    rep.on("change", handler);
    return () => {
      rep.removeListener?.("change", handler);
    };
  }, [name]);

  const setter = (next: T) => {
    const nodecg = getNodeCG();
    if (!nodecg) return;
    nodecg.Replicant<T>(name).value = next;
  };

  return [value, setter];
}
